import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { isApiRequest } from './api-url';
import { isAuthSkipRefresh } from './api-endpoints';

const DEVICE_KEY = 'many_tech_device_id';

function deviceId(): string {
  const existing = localStorage.getItem(DEVICE_KEY);
  if (existing) {
    return existing;
  }
  const created = crypto.randomUUID();
  localStorage.setItem(DEVICE_KEY, created);
  return created;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url)) {
    return next(req);
  }

  const auth = inject(AuthService);
  const token = auth.getAccessToken();
  const headers: Record<string, string> = {
    'x-device-id': deviceId(),
    'x-api-version': 'v1',
    platform: 'web',
    'accept-language': 'en',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const authorized = req.clone({ setHeaders: headers, withCredentials: true });
  return next(authorized).pipe(
    catchError((error: HttpErrorResponse) => {
      const skipRefresh = isAuthSkipRefresh(req.url);
      if (error.status !== 401 || skipRefresh) {
        return throwError(() => error);
      }
      return from(auth.refreshAccessToken()).pipe(
        switchMap((nextToken) => {
          const retried = req.clone({
            setHeaders: {
              ...headers,
              Authorization: `Bearer ${nextToken}`,
            },
            withCredentials: true,
          });
          return next(retried);
        }),
        catchError((refreshError) => throwError(() => refreshError)),
      );
    }),
  );
};
